// 手機觸控除錯記錄：會議網址加 ?debug=1 才啟用。
//
// 每次碰螢幕都記下：手指座標、收到事件的元素、那個座標最上層的元素、
// 頁面縮放倍率（visualViewport.scale）與畫面尺寸／方向，每秒批次送到
// /api/debug/touch。電腦開 /api/debug/touch/view 即時看。
//
// 會議頁本身、Jitsi iframe、白板 iframe 三個文件都會掛上監聽（後兩者同源才掛得上）。
// 白板每次打開都是新的 iframe，所以每秒檢查一次有沒有新的文件要掛。

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const TYPES = ["touchstart", "touchend", "pointerdown", "click"];

function describe(el) {
  if (!el || !el.tagName) return el ? String(el.nodeName || el) : "（無）";
  let s = el.tagName.toLowerCase();
  if (el.id) s += "#" + el.id;
  if (typeof el.className === "string" && el.className.trim()) {
    s += "." + el.className.trim().split(/\s+/).slice(0, 3).join(".");
  }
  const label = el.getAttribute("aria-label") || (el.textContent || "").trim();
  if (label) s += " 「" + label.slice(0, 16) + "」";
  return s;
}

function viewport() {
  const v = window.visualViewport;
  return {
    vv: v ? { scale: Math.round(v.scale * 100) / 100, ox: Math.round(v.offsetLeft), oy: Math.round(v.offsetTop) } : null,
    inner: `${window.innerWidth}x${window.innerHeight}`,
    // 直接看畫面寬高：screen.orientation 在 iPhone 上轉向後常會慢一拍
    orient: window.innerWidth > window.innerHeight ? "橫向" : "直向",
  };
}

export function startTouchDebug(getFrames) {
  const session = Math.random().toString(36).slice(2, 7);
  let queue = [];
  const detach = [];
  const attached = new WeakSet();

  const record = (src, doc) => (e) => {
    const p = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]) || e;
    const x = p.clientX, y = p.clientY;
    let top = null;
    try { top = doc.elementFromPoint(x, y); } catch (err) {}
    // 被點到的元素實際的上下邊界：手指 y 不在這個範圍內，就是「畫面跟點擊位置錯開」
    let ty = "";
    try {
      const r = e.target.getBoundingClientRect();
      ty = `${Math.round(r.top)}–${Math.round(r.bottom)}`;
    } catch (err) {}
    queue.push({
      t: Date.now(), src, type: e.type,
      x: Math.round(x), y: Math.round(y),
      target: describe(e.target), top: describe(top), ty,
      // 整頁與事件所在文件（Jitsi／白板 iframe）各自的捲動量；正常都應該是 0
      sy: Math.round(window.scrollY),
      dsy: Math.round((doc.defaultView && doc.defaultView.scrollY) || 0),
      ...viewport(),
    });
  };

  const attach = (src, win) => {
    if (!win || attached.has(win)) return;
    attached.add(win);
    const doc = win.document;
    const handler = record(src, doc);
    TYPES.forEach((type) => doc.addEventListener(type, handler, { capture: true, passive: true }));
    detach.push(() => TYPES.forEach((type) => doc.removeEventListener(type, handler, { capture: true })));
  };

  attach("page", window);
  const scan = setInterval(() => {
    for (const [src, frame] of getFrames()) {
      try {
        const win = frame && frame.contentWindow;
        if (win && win.document && win.document.readyState !== "loading") attach(src, win);
      } catch (err) {
        // 不同來源的 iframe 讀不到，略過
      }
    }
  }, 1000);

  // 縮放、轉向也記下來：頁面被放大往往就是點不到的原因
  const onViewport = () => queue.push({ t: Date.now(), src: "page", type: "viewport", ...viewport() });
  window.visualViewport && window.visualViewport.addEventListener("resize", onViewport);
  window.addEventListener("orientationchange", onViewport);
  onViewport();

  const flush = () => {
    if (!queue.length) return;
    const events = queue;
    queue = [];
    fetch(`${API_BASE_URL}/api/debug/touch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session, device: navigator.userAgent, events }),
      keepalive: true,
    }).catch(() => {});
  };
  const timer = setInterval(flush, 1000);

  // 讓拿手機的人知道記錄開著，也看得到自己是哪個代號（電腦頁面用這個代號篩選）
  const badge = document.createElement("div");
  badge.textContent = `觸控記錄中 ${session}`;
  Object.assign(badge.style, {
    position: "fixed", top: "4px", left: "4px", zIndex: "2147483647",
    padding: "2px 8px", borderRadius: "4px", background: "#d00", color: "#fff",
    font: "12px/1.6 sans-serif", pointerEvents: "none",
  });
  document.body.appendChild(badge);

  return () => {
    clearInterval(scan);
    clearInterval(timer);
    flush();
    detach.forEach((fn) => fn());
    window.visualViewport && window.visualViewport.removeEventListener("resize", onViewport);
    window.removeEventListener("orientationchange", onViewport);
    badge.remove();
  };
}
